import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { getTranslation } from '../i18n/translations';

const tt = (key) => getTranslation(localStorage.getItem('app-language') || 'tr', key);

const escape = (s) =>
  String(s ?? '').replace(/[&<>"]/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
  }[c]));

// Side margins are fixed at 20mm (must match backend letterhead.side_margin_mm()).
const SIDE_MARGIN_MM = 20;

/**
 * Reads letterhead settings from the localStorage cache `nkt_company_settings`
 * which is kept in sync with `/api/user/settings` by SettingsPage. Returns an
 * empty object if unavailable so callers can spread it safely.
 */
export function getLetterheadFromCache() {
  try {
    const raw = localStorage.getItem('nkt_company_settings');
    if (!raw) return {};
    const s = JSON.parse(raw);
    return {
      letterhead_image: s.letterhead_image || undefined,
      letterhead_top_mm:
        typeof s.letterhead_top_mm === 'number' ? s.letterhead_top_mm : undefined,
      letterhead_bottom_mm:
        typeof s.letterhead_bottom_mm === 'number' ? s.letterhead_bottom_mm : undefined,
      // Also surface company/contact fields so the document body can fall back
      // to global settings when the document itself didn't store them.
      company_name: s.companyName || undefined,
      company_phone: s.phone || undefined,
      company_email: s.email || undefined,
      company_address: s.address || undefined,
      logo: s.logo || undefined,
    };
  } catch (_) {
    return {};
  }
}

/**
 * Builds the inner HTML for a printable/PDF-exportable text document:
 * logo (top-right) + Firma Bilgileri + Müşteri Bilgileri + content.
 * Suppresses the in-content header when the user has uploaded a custom
 * letterhead, since the letterhead already carries those visuals.
 */
export function buildTextDocumentBodyHTML(doc, { suppressHeader = false } = {}) {
  const logoHTML =
    !suppressHeader && doc.logo
      ? `<div style="text-align:right;margin-bottom:12px;">
           <img src="${doc.logo}" alt="Logo" style="max-width:80px;max-height:80px;object-fit:contain;" crossorigin="anonymous" />
         </div>`
      : '';

  const hasCompany =
    doc.company_name || doc.company_phone || doc.company_email || doc.company_address;
  const hasCustomer =
    doc.customer_name || doc.customer_phone || doc.customer_email || doc.customer_address;

  // When a custom letterhead is active we only render the customer block
  // (since the letterhead itself already shows the company info).
  const showCompany = !suppressHeader && hasCompany;
  const showCustomer = hasCustomer;

  const headerHTML =
    showCompany || showCustomer
      ? `<div style="display:flex;justify-content:space-between;gap:24px;margin-bottom:20px;padding-bottom:14px;border-bottom:2px solid #333;font-size:12px;line-height:1.55;">
           <div style="flex:1;">
             ${showCompany ? `<div style="font-weight:700;color:#1a365d;margin-bottom:6px;">${tt('pdfCompanyInfo')}</div>` : ''}
             ${showCompany && doc.company_name ? `<div><strong>${escape(doc.company_name)}</strong></div>` : ''}
             ${showCompany && doc.company_phone ? `<div>Tel: ${escape(doc.company_phone)}</div>` : ''}
             ${showCompany && doc.company_email ? `<div>E-posta: ${escape(doc.company_email)}</div>` : ''}
             ${showCompany && doc.company_address ? `<div>${escape(doc.company_address)}</div>` : ''}
           </div>
           <div style="flex:1;text-align:right;">
             ${showCustomer ? `<div style="font-weight:700;color:#1a365d;margin-bottom:6px;">${tt('pdfCustomerInfo')}</div>` : ''}
             ${showCustomer && doc.customer_name ? `<div><strong>${escape(doc.customer_name)}</strong></div>` : ''}
             ${showCustomer && doc.customer_phone ? `<div>Tel: ${escape(doc.customer_phone)}</div>` : ''}
             ${showCustomer && doc.customer_email ? `<div>E-posta: ${escape(doc.customer_email)}</div>` : ''}
             ${showCustomer && doc.customer_address ? `<div>${escape(doc.customer_address)}</div>` : ''}
           </div>
         </div>`
      : '';

  return `${logoHTML}${headerHTML}<div>${doc.content || ''}</div>`;
}

/**
 * Builds a complete <html> document string for printing/preview, including
 * the user's uploaded letterhead as a full-page background image if any.
 */
export function buildTextDocumentPrintHTML(doc) {
  const cache = getLetterheadFromCache();
  // Fill in any missing letterhead/company fields from the cache
  Object.keys(cache).forEach((k) => {
    if (doc[k] == null || doc[k] === '') doc[k] = cache[k];
  });
  const fontSize = doc.font_size || 14;
  const name = doc.name || 'Belge';
  const hasLetterhead = !!doc.letterhead_image;
  const topMm = hasLetterhead ? (doc.letterhead_top_mm ?? 40) : 25;
  const bottomMm = hasLetterhead ? (doc.letterhead_bottom_mm ?? 25) : 20;

  const letterheadBg = hasLetterhead
    ? `<img src="${doc.letterhead_image}" alt="" crossorigin="anonymous" style="position:absolute;inset:0;width:100%;height:100%;object-fit:fill;z-index:0;pointer-events:none;" />`
    : '';

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>${escape(name)}</title>
  <style>
    @page { size: A4; margin: 0; }
    html, body { margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: ${fontSize}px; line-height: 1.6; color: #000; }
    .a4 { position: relative; width: 210mm; min-height: 297mm; background: #fff; box-sizing: border-box; }
    .ctx {
      position: relative; z-index: 1;
      padding: ${topMm}mm ${SIDE_MARGIN_MM}mm ${bottomMm}mm ${SIDE_MARGIN_MM}mm;
    }
    img { max-width: 100%; }
  </style>
</head>
<body><div class="a4">${letterheadBg}<div class="ctx">${buildTextDocumentBodyHTML(doc, { suppressHeader: hasLetterhead })}</div></div></body>
</html>`;
}

/**
 * Renders the given text document on a hidden A4-sized container (with
 * letterhead background + user-controlled top/bottom padding) and converts
 * to PDF via html2canvas + jsPDF.
 *
 * @param {Object} doc - { name, content, font_size, logo, company_*, customer_*,
 *                         letterhead_image, letterhead_top_mm, letterhead_bottom_mm }
 * @param {{ print?: boolean }} opts - if print=true, opens the print dialog
 *                                     instead of downloading.
 */
export async function exportTextDocumentToPDF(doc, { print = false } = {}) {
  const cache = getLetterheadFromCache();
  Object.keys(cache).forEach((k) => {
    if (doc[k] == null || doc[k] === '') doc[k] = cache[k];
  });

  const fontSize = doc.font_size || 14;
  const name = doc.name || 'Belge';
  const hasLetterhead = !!doc.letterhead_image;
  const topMm = hasLetterhead ? (doc.letterhead_top_mm ?? 40) : 25;
  const bottomMm = hasLetterhead ? (doc.letterhead_bottom_mm ?? 25) : 20;

  const container = document.createElement('div');
  container.style.cssText =
    'position:fixed;left:-99999px;top:0;width:210mm;min-height:297mm;background:#fff;box-sizing:border-box;z-index:-1;overflow:hidden;';

  if (hasLetterhead) {
    const bg = document.createElement('img');
    bg.src = doc.letterhead_image;
    bg.alt = '';
    bg.crossOrigin = 'anonymous';
    bg.style.cssText =
      'position:absolute;inset:0;width:100%;height:100%;object-fit:fill;z-index:0;pointer-events:none;';
    container.appendChild(bg);
  }

  const editorDiv = document.createElement('div');
  editorDiv.style.cssText =
    `position:relative;z-index:1;width:100%;min-height:297mm;box-sizing:border-box;` +
    `font-family:Arial,sans-serif;font-size:${fontSize}px;line-height:1.6;color:#000;` +
    `padding:${topMm}mm ${SIDE_MARGIN_MM}mm ${bottomMm}mm ${SIDE_MARGIN_MM}mm;`;
  editorDiv.innerHTML = buildTextDocumentBodyHTML(doc, { suppressHeader: hasLetterhead });

  container.appendChild(editorDiv);
  document.body.appendChild(container);

  try {
    // Wait for images (logo + letterhead) to load before snapshotting
    await new Promise((r) => setTimeout(r, 80));
    const imgs = container.querySelectorAll('img');
    await Promise.all(
      Array.from(imgs).map((img) => {
        if (img.complete && img.naturalWidth > 0) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
          setTimeout(resolve, 2000); // safety
        });
      })
    );

    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    });

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgData = canvas.toDataURL('image/png');
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }

    if (print) {
      const blob = pdf.output('blob');
      const url = window.URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = url;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow?.print();
          setTimeout(() => {
            document.body.removeChild(iframe);
            window.URL.revokeObjectURL(url);
          }, 1000);
        }, 500);
      };
    } else {
      pdf.save(`${name}.pdf`);
    }
  } finally {
    document.body.removeChild(container);
  }
}
